import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { useVisit } from "../../../contexts/VisitContext";
import { Textarea } from "../../../design/textarea";
import { Field, Label } from "../../../design/fieldset";
import { Select } from "../../../design/select";
import { NoShowParty, NO_SHOW_PARTY_LABELS } from "@visitproof/types";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../../../contexts/AuthContext";

interface Props {
  visitId: string;
}

const NO_SHOW_OPTIONS: NoShowParty[] = [
  "visiting_parent",
  "custodial_parent",
  "both_parents",
  "child",
  "supervisor",
  "other",
];

const MarkNoShowButton: React.FC<Props> = ({ visitId }) => {
  const { updateVisit } = useVisit();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noShowParty, setNoShowParty] = useState<NoShowParty | "">("");
  const [notes, setNotes] = useState("");

  const handleSubmit = async () => {
    if (!noShowParty) return;

    setIsSaving(true);
    try {
      await updateVisit(visitId, {
        status: "no_show",
        noShowDetails: {
          noShowParty,
          notes: notes.trim() || undefined,
          recordedAt: Timestamp.now(),
          recordedBy: user?.uid,
        },
      });
      setIsOpen(false);
      setNoShowParty("");
      setNotes("");
    } catch (error) {
      console.error("Error marking visit as no-show:", error);
      alert("Failed to mark visit as no-show. Please try again.");
    }
    setIsSaving(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setNoShowParty("");
    setNotes("");
  };

  return (
    <>
      <Button type="button" color="amber" onClick={() => setIsOpen(true)}>
        No Show
      </Button>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Mark as No Show</DialogTitle>
        <DialogBody>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
            Please indicate who did not show up for this visit. This information helps track attendance patterns for reporting.
          </p>
          <div className="space-y-4">
            <Field>
              <Label>Who didn't show up?</Label>
              <Select
                value={noShowParty}
                onChange={(e) => setNoShowParty(e.target.value as NoShowParty)}
                disabled={isSaving}
              >
                <option value="">Select...</option>
                {NO_SHOW_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {NO_SHOW_PARTY_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional details about the no-show..."
                rows={3}
                disabled={isSaving}
              />
            </Field>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="amber" onClick={handleSubmit} disabled={isSaving || !noShowParty}>
            {isSaving ? "Saving..." : "Mark No Show"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarkNoShowButton;
